import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Player from '../Player/Player';

import './VideoDetail.scss';

const auth = getAuthClient();

const VIDEO_INSTRUCTIONS = {
  Wistia: 'Use the player controls to play, pause, adjust volume, or enter fullscreen.',
  YouTube: 'Use the player controls to play, pause, adjust volume, or enter fullscreen. You may also view this video directly on YouTube.',
  Vimeo: 'Use the player controls to play, pause, or adjust volume.',
};

const VideoDetail = ({
  id,
  className,
  videoId,
  service,
  uuid,
  autoplay,
  isLoading,
  showInstructions,
}) => {
  const [videoData, setVideoData] = useState(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  // Fetch video data if uuid provided
  useEffect(() => {
    if (uuid) {
      const fetchData = async () => {
        const url = buildYourTourApiUrl(`/your_tour_api/video/${uuid}`);
        const response = await auth.fetchWithAuthentication(
          url,
          'GET',
        );
        if (isMountedRef.current && response && !response.error) {
          setVideoData(response);
        }
      };
      fetchData();
    }
  }, [uuid]);

  // Use fetched data or props
  const actualVideoId = videoId || videoData?.serviceVideoId;
  const actualService = service || videoData?.service;

  // Return null only if we have no way to get video data and we're not loading
  if (!actualVideoId && !actualService && !uuid && !isLoading) return null;

  let url = null;
  if (actualService) {
    switch (actualService) {
      case 'Wistia':
        url = `//fast.wistia.net/embed/iframe/${actualVideoId}`;
        break;
      case 'YouTube':
        url = `https://www.youtube.com/watch?v=${actualVideoId}`;
        break;
      case 'Vimeo':
        url = `https://player.vimeo.com/video/${actualVideoId}`;
        break;
      default:
    }
  }

  return (actualVideoId && actualService) || uuid || isLoading ? (
    <div
      id={id || ''}
      className={className || ''}
    >
      <div className="video__placeholder">

        <LoadingSpinner />

        {url && (
          <Player
            url={url}
            className="video__player"
            autoplay={autoplay}
          />
        )}
      </div>

      {showInstructions && (
        <div className="detail__footnote">
          {VIDEO_INSTRUCTIONS[actualService] || VIDEO_INSTRUCTIONS.Wistia}
        </div>
      )}
    </div>
  ) : null;
};

VideoDetail.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  videoId: PropTypes.string,
  service: PropTypes.string,
  uuid: PropTypes.string,
  autoplay: PropTypes.bool,
  isLoading: PropTypes.bool,
  showInstructions: PropTypes.bool,
};

VideoDetail.defaultProps = {
  id: null,
  className: null,
  videoId: null,
  service: null,
  uuid: null,
  autoplay: false,
  isLoading: false,
  showInstructions: false,
};

export default VideoDetail;
